import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IconArrowUpRight,
  IconBlocks,
  IconBook,
  IconBook2,
  IconBuildingBank,
  IconCalculator,
  IconClipboardList,
  IconCoins,
  IconLayoutGrid,
  IconMicroscope,
  IconScissors,
  IconSearch,
  IconShieldCheck,
  IconSparkles,
  IconStack2,
  IconStethoscope,
} from "@tabler/icons-react";
import type { EncoderTabId } from "../../types/encoder";
import { cn, encoderHref } from "../../utils/encoder";

type GridVariant = "home" | "menu";

type IconComponent = typeof IconSearch;

type ToolItem = {
  key: string;
  route: EncoderTabId;
  title: string;
  description: string;
  icon: IconComponent;
  highlight: boolean;
  badge?: string;
};

type ToolGroup = {
  id: string;
  title: string;
  description: string;
  icon: IconComponent;
  tools: ToolItem[];
};

const GROUPS: ToolGroup[] = [
  {
    id: "encoders",
    title: "Encoders & Code Sets",
    description: "Integrated logic encoders to search, build, and edit codes.",
    icon: IconBlocks,
    tools: [
      {
        key: "code-search",
        route: "code-search",
        title: "Global Code Search",
        description:
          "Look up ICD-10-CM, PCS, CPT®, and HCPCS codes in one place.",
        icon: IconSearch,
        highlight: true,
      },
      {
        key: "cm-explorer",
        route: "cm-explorer",
        title: "ICD-10-CM Builder",
        description:
          "Search, validate, and build clinical diagnosis codes step by step.",
        icon: IconStethoscope,
        highlight: true,
      },
      {
        key: "pcs-explorer",
        route: "pcs-explorer",
        title: "ICD-10-PCS Builder",
        description:
          "Search and construct inpatient facility procedure codes by character.",
        icon: IconScissors,
        highlight: true,
      },
      {
        key: "cpt-explorer",
        route: "cpt-explorer",
        title: "CPT® / HCPCS Editor",
        description:
          "Integrated claim builder with real-time NCCI/OCE edits, MCD rules, modifiers, and RVU/pricing.",
        icon: IconClipboardList,
        highlight: true,
        badge: "ALL-IN-ONE",
      },
    ],
  },
  {
    id: "references",
    title: "References & Compliance",
    description: "Official guidelines, clinical criteria, and edit lookups.",
    icon: IconBook2,
    tools: [
      {
        key: "coding-guide",
        route: "coding-guide",
        title: "Coding Guide",
        description: "Browse CMS, AHA Coding Clinic, and AMA conventions.",
        icon: IconBook,
        highlight: true,
      },
      {
        key: "clinical-criteria",
        route: "clinical-criteria",
        title: "Clinical Criteria",
        description:
          "Review standard disease definitions and supporting clinical evidence.",
        icon: IconMicroscope,
        highlight: true,
      },
      {
        key: "editor",
        route: "editor",
        title: "Code Editor & NCCI Edits",
        description:
          "Build code lists and review ICD validation and Medicare NCCI PTP edits.",
        icon: IconStack2,
        highlight: true,
      },
      {
        key: "coverage-policy-search",
        route: "mcd-coverage",
        title: "Coverage Policy Search",
        description:
          "Search CMS National and Local Coverage Determinations by procedure.",
        icon: IconShieldCheck,
        highlight: false,
      },
    ],
  },
  {
    id: "groupers",
    title: "Groupers & Reimbursement",
    description: "Calculate claim weights and review CMS coverage databases.",
    icon: IconCoins,
    tools: [
      {
        key: "ms-drg-grouper",
        route: "ms-drg-grouper",
        title: "MS-DRG Grouper",
        description: "Calculate MS-DRG assignments from encounter details.",
        icon: IconCalculator,
        highlight: true,
      },
      {
        key: "mcd-coverage",
        route: "mcd-coverage",
        title: "Medicare Coverage Database (MCD)",
        description:
          "Search official CMS National and Local Coverage Determinations.",
        icon: IconBuildingBank,
        highlight: true,
      },
    ],
  },
];

export const EncoderToolGrid: React.FC<{
  variant: GridVariant;
  current?: EncoderTabId | null;
  onSelect?: () => void;
}> = ({ variant, current, onSelect }) => {
  const home = variant === "home";

  return (
    <div
      className={cn(
        "flex overflow-hidden bg-[#f4f2ff] text-[#3f3b4c]",
        home
          ? "flex-col rounded-2xl border border-violet-200/80 text-base shadow-xl shadow-violet-950/10"
          : "text-sm"
      )}
    >
      <div
        className={cn(
          "grid min-h-0 flex-1 grid-cols-1 divide-y divide-violet-200/80 overflow-y-auto md:grid-cols-3 md:divide-x md:divide-y-0",
          !home && "max-h-[calc(100vh-7rem)]"
        )}
      >
        {GROUPS.map((group) => {
          const GroupIcon = group.icon;
          return (
            <section
              key={group.id}
              className={home ? "p-7" : "p-3"}
            >
              <div
                className={cn(
                  "flex items-center gap-3 border-b-2 border-violet-200",
                  home ? "mb-4 h-24 px-3" : "mb-2 h-[4.75rem] px-2"
                )}
              >
                <div
                  className={cn(
                    "flex shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#3f087f] via-[#a314e8] to-[#ff925d] text-white shadow-sm shadow-violet-900/20",
                    home ? "size-10" : "size-8"
                  )}
                >
                  <GroupIcon className={home ? "size-5" : "size-4"} />
                </div>
                <div className="min-w-0">
                  <h2
                    className={cn(
                      "font-extrabold tracking-tight text-[#2b1a4d]",
                      home ? "text-lg" : "text-sm"
                    )}
                  >
                    {group.title}
                  </h2>
                  <p
                    className={cn(
                      "text-[#6b6679]",
                      home ? "text-sm" : "text-xs"
                    )}
                  >
                    {group.description}
                  </p>
                </div>
              </div>
              <ul className="flex flex-col gap-1">
                {group.tools.map((tool) => {
                  const ToolIcon = tool.icon;
                  const isCurrent =
                    Boolean(tool.highlight && current && current === tool.route);
                  return (
                    <li key={tool.key}>
                      <Link
                        to={encoderHref(tool.route)}
                        aria-current={isCurrent ? "page" : undefined}
                        onClick={onSelect}
                        className={cn(
                          "group flex flex-row items-start gap-3 rounded-xl border transition-all duration-150",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a314e8]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f4f2ff]",
                          home ? "px-3 py-4" : "px-2 py-2",
                          isCurrent
                            ? "border-[#c9a6ff] bg-white shadow-md shadow-violet-900/10"
                            : "border-transparent hover:border-violet-200 hover:bg-white hover:shadow-sm active:scale-[0.99]"
                        )}
                      >
                        <span
                          className={cn(
                            "flex shrink-0 items-center justify-center rounded-lg ring-1 transition-colors",
                            home ? "size-10" : "size-8",
                            isCurrent
                              ? "bg-[#efe5ff] text-[#7200c9] ring-[#c9a6ff]"
                              : "bg-white text-[#7200c9] ring-violet-200 group-hover:bg-[#f0e8ff]"
                          )}
                        >
                          <ToolIcon className={home ? "size-5" : "size-4"} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={cn(
                                "font-bold text-[#2b1a4d] group-hover:text-[#7200c9]",
                                isCurrent && "text-[#7200c9]"
                              )}
                            >
                              {tool.title}
                            </span>
                            {tool.badge ? (
                              <span
                                className={cn(
                                  "rounded-full bg-[#e3f0ff] px-1.5 py-0.5 font-bold uppercase tracking-wide text-[#1f63b3]",
                                  home ? "text-[10px]" : "text-[9px]"
                                )}
                              >
                                {tool.badge}
                              </span>
                            ) : null}
                          </span>
                          <span className="mt-0.5 block text-[#6b6679]">
                            {tool.description}
                          </span>
                        </span>
                        <IconArrowUpRight
                          className={cn(
                            "mt-1 size-4 shrink-0 text-[#7200c9] opacity-0 transition-all duration-150 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100",
                            "-translate-x-1"
                          )}
                        />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export const EncoderToolsHome: React.FC = () => (
  <div className="relative flex min-h-0 flex-1 overflow-auto bg-[radial-gradient(ellipse_at_top,_#f4f0ff_0%,_#ffffff_55%)]">
    <div className="m-auto flex w-[80%] flex-col gap-6 py-10">
      <p className="inline-flex w-fit items-center gap-1.5 rounded-full border border-violet-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#7200c9] shadow-sm">
        <IconSparkles size={14} />
        BulwarkHealth coding workspace
      </p>
      <EncoderToolGrid variant="home" />
    </div>
  </div>
);

export const EncoderToolsMenu: React.FC<{
  current?: EncoderTabId | null;
}> = ({ current }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        aria-label="Open encoder tools menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "inline-flex h-9 items-center gap-1 rounded-full border border-violet-300 bg-white px-2 text-sm font-bold text-[#7200c9] shadow-sm sm:gap-1.5 sm:px-4",
          "hover:border-[#7200c9] hover:bg-[#f0e8ff] hover:text-[#4b087f]",
          open && "border-[#7200c9] bg-[#f0e8ff] text-[#4b087f]"
        )}
      >
        <IconLayoutGrid size={16} />
        <span className="hidden sm:inline">Encoder tools</span>
      </button>
      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-violet-200 bg-[#f4f2ff] shadow-2xl shadow-violet-950/20 md:w-[min(1040px,calc(100vw-2rem))]">
          <EncoderToolGrid
            variant="menu"
            current={current}
            onSelect={() => setOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
};
